import React, { useEffect } from 'react'
import { X, FileText, RotateCcw, BarChart3, Trash2, ExternalLink } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Session } from '@/types'

interface MobileSessionActionSheetProps {
  isOpen: boolean
  session: Session | null
  onClose: () => void
  onViewDetails: () => void
  onReopen: () => void
  onGenerateReport: () => void
  onDelete: () => void
}

/**
 * Bottom sheet action menu for session actions on mobile
 */
const MobileSessionActionSheet: React.FC<MobileSessionActionSheetProps> = ({
  isOpen,
  session,
  onClose,
  onViewDetails,
  onReopen,
  onGenerateReport,
  onDelete,
}) => {
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen || !session) return null

  const roomName = session.room_name || 'Unknown Room'
  const sessionDate = session.session_date
    ? new Date(session.session_date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : ''

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Action Sheet */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50',
          'bg-white rounded-t-2xl shadow-2xl',
          'animate-in slide-in-from-bottom duration-300',
          'safe-area-bottom'
        )}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{roomName}</h3>
              <p className="text-sm text-gray-500">{sessionDate}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="py-2">
          {/* View Details */}
          <button
            onClick={() => {
              onClose()
              onViewDetails()
            }}
            className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-gray-100 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <ExternalLink className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900">Open Session</p>
              <p className="text-sm text-gray-500">View and edit session details</p>
            </div>
          </button>

          {/* Generate Report */}
          <button
            onClick={() => {
              onClose()
              onGenerateReport()
            }}
            className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-gray-100 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900">Generate Report</p>
              <p className="text-sm text-gray-500">Create inventory report</p>
            </div>
          </button>

          {/* Reopen */}
          <button
            onClick={() => {
              onClose()
              onReopen()
            }}
            className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-gray-100 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900">Reopen Session</p>
              <p className="text-sm text-gray-500">Delete and create new session</p>
            </div>
          </button>

          {/* Divider */}
          <div className="border-t border-gray-100 my-2" />

          {/* Delete */}
          <button
            onClick={() => {
              onClose()
              onDelete()
            }}
            className="w-full flex items-center gap-4 px-4 py-3.5 active:bg-red-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-red-600">Delete Session</p>
              <p className="text-sm text-gray-500">Permanently remove this session</p>
            </div>
          </button>
        </div>

        {/* Cancel Button */}
        <div className="px-4 pb-4 pt-2">
          <button
            onClick={onClose}
            className={cn(
              'w-full py-3.5 rounded-xl font-medium',
              'bg-gray-100 text-gray-700',
              'active:bg-gray-200 transition-colors'
            )}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  )
}

export default MobileSessionActionSheet
